import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Anchor, ArrowLeft, ChevronLeft, ChevronRight, Dices, Gift, Headphones,
  Home, LogIn, LogOut, Megaphone, Menu, Moon, Phone, PlayCircle, Plus,
  Rocket, Search, ShieldCheck, Sun, Target, Ticket, Trophy, User, Wallet, X,
} from 'lucide-react';
import Logo from '../../components/Logo';
import UserDropdown from '../../components/UserDropdown';
import AuthModal from '../../components/AuthModal';
import CallbackModal from '../../components/CallbackModal';
import GameSearch from '../../components/GameSearch';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import VegaFooter from './VegaFooter';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { buildLiveChatConfig, openLiveChat } from '../../components/liveChatConfig';
import { dockSupportWidgets } from '../../lib/floatingWidgets';
import { useHiddenCategories } from '../../lib/gameCategories';
import {
  bottomNavIcon, pathOf, splitBottomNav, useBottomNavItems,
} from '../../lib/bottomNav';
import { topNavIcon, useTopNavLinks } from '../../lib/topNav';
import { renderSettingText } from '../../lib/textTemplate';
import {
  VEGA_MODE_KEY, applyVegaMode, readVegaMode, storeVegaMode,
} from './vegaMode';
import './vega.css';
import { adminShortcutVisible } from '../../lib/adminDomain';
// Panelden yazılan CSS'in tutunacağı sabit kimlikler (bkz. lib/domHooks.js):
// kimlik şemaya gömülmez, ortak sabitten okunur.
import { SHELL_IDS } from '../../lib/domHooks';

// Referans arayüzün kabuğu iki gezinme yolunu **birlikte** taşır: üstte
// ikonlu kategori barı, solda hamburgerle açılan offcanvas menü. İkincisi
// birincinin mobil karşılığı değildir — masaüstünde de açılır ve panelden
// gelen kısayolları, sosyal bağlantıları ve görünüm anahtarını orada toplar.
//
// Kategoriler kabuğun kendi listesindedir; kısayollar **sabit kodlu
// değildir**, panelde Footer Yönetimi → "Üst menüde de göster" işaretlenen
// menü grubundan gelir (lib/topNav.js).
const NAV_ITEMS = [
  { to: '/', label: 'Anasayfa', icon: Home, end: true },
  { to: '/spor', label: 'Spor', icon: Trophy, sports: true },
  { to: '/slotlar', label: 'Slot', icon: Dices, category: 'slot' },
  { to: '/canli-casino', label: 'Canlı Casino', icon: PlayCircle, category: 'live-casino' },
  { to: '/crash', label: 'Hızlı Oyunlar', icon: Rocket, category: 'crash' },
  { to: '/promosyonlar', label: 'Promosyonlar', icon: Gift },
  // Çark ve çapa kategori değildir; kendi bayrakları açıkken görünürler ve
  // panelden footer menüsüne de eklenmişlerse buradaki kopya düşer (aşağıda).
  { to: '/cark', label: 'Şans Çarkı', icon: Target, flag: 'wheel_enabled', optional: true },
  { to: '/capa', label: 'Çapa', icon: Anchor, flag: 'anchor_enabled', optional: true },
  // Çekiliş kategori değildir; yayında bir çekiliş varken görünür
  // (``raffle_active`` çalışma anındaki durumdur, bkz. CLAUDE.md).
  { to: '/cekilis', label: 'Çekiliş', icon: Ticket, flag: 'raffle_active', optional: true },
  { to: '/etkinlik', label: 'Tahmin', icon: Target, flag: 'prediction_active', optional: true },
];

// Offcanvas menünün alt bölümleri **sabit kodlu değildir**: referansta
// kategorilerin ardından başlıklı gruplar gelir ve bu projede onların
// karşılığı panelden yönetilen footer menü gruplarıdır (Footer Yönetimi).
// Üst menüdeki kısayol şeridi yalnızca "Üst menüde de göster" işaretli
// grupları taşır; sol menü hepsini gösterir — referansın sol menüsü de
// sitenin tam haritasıdır.

const VegaLayout = ({ children }) => {
  const { user, balance, bonusBalance, logout } = useAuth();
  const settings = useSettings();
  // Panel başka bir alan adına taşındıysa kısayol çizilmez: adresi
  // bilmediğimiz için bağlantı kurulamaz ve panelin varlığını duyurmanın
  // da anlamı yoktur (bkz. lib/adminDomain.js).
  const showAdminLink = adminShortcutVisible(settings);
  const nav = useNavigate();
  const location = useLocation();
  // Üstteki hızlı arama kutusundan seçilen oyun doğrudan açılabilsin diye
  // kabuk kendi başlatıcısını taşır.
  const launcher = useGameLauncher();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [noticeClosed, setNoticeClosed] = useState(false);
  // Görünüm oyuncunun tercihidir ve tarayıcısında kalır; sitenin tasarım
  // ayarını değiştirmez (bkz. themes/vega/vegaMode.js).
  const [mode, setMode] = useState(readVegaMode);
  const hiddenCats = useHiddenCategories();

  // Kategori barı sayfaya sığıyorsa kaydırma okları gizlenir; mobilde ok yok,
  // orada kaydırılabildiğini uçlardaki solma anlatır.
  const navRef = useRef(null);
  const [navOverflow, setNavOverflow] = useState(false);
  const [navEdges, setNavEdges] = useState({ left: false, right: false });

  const callbackEnabled = settings.callback_enabled !== false;
  // Menüdeki "Canlı Destek" düğmesi widget'ın kendisiyle aynı koşula bağlıdır:
  // panelden kapatıldığında (ya da lisans girilmediğinde) widget hiç yüklenmez.
  const liveChatEnabled = useMemo(
    () => Boolean(buildLiveChatConfig(settings, user)),
    [settings, user],
  );

  // Görünüm `<html>` üzerine de yazılır: kabuğun dışında kalan tek yüzey
  // olan `<body>` zeminini ancak oradan boyayabiliriz.
  useEffect(() => {
    applyVegaMode(mode);
    return () => applyVegaMode('dark');
  }, [mode]);

  // Panelden gelen destek kodu kendi düğümünü `<body>`ye bırakır ve bir kısmı
  // onu konumlandırmaz: bant sayfanın en üstüne, kabuğun da üstüne düşer.
  // Yerleştirme onu alt barın üstündeki sağ köşeye alır; kendi kendini
  // konumlandıran widget yerinde bırakılır, yalnızca barın üstüne çıkarılır
  // (bkz. lib/floatingWidgets.js).
  useEffect(() => dockSupportWidgets(), []);

  // Aynı tarayıcının başka sekmesinde görünüm değişirse bu sekme de uyar.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === VEGA_MODE_KEY) setMode(readVegaMode());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('reset_token')) {
      setAuthTab('reset');
      setAuthOpen(true);
    }
  }, []);

  useEffect(() => {
    if (location.state?.auth !== 'login' || user) return;
    setAuthTab('login');
    setAuthOpen(true);
    nav(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, nav, user]);

  // Sayfa değişince offcanvas kapanır.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Menü açıkken arkadaki sayfa kaymasın.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [drawerOpen]);

  // Esc menüyü kapatır: referansta offcanvas'ın kendi davranışı budur.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Kategorilerin ardındaki kısayollar footer menüsünden gelir; kapalı bölüme
  // (spor, kategori, şans çarkı, çapa) giden bağlantıları hook'un kendisi ayıklar.
  const extraNav = useTopNavLinks();

  // Aynı adres hem kabuk listesinde hem panelde işaretliyse iki kez çizilirdi;
  // yöneticinin yazdığı etiket kazanır, kabuğun kopyası düşer.
  const shortcutPaths = useMemo(() => new Set(
    extraNav.filter((item) => !item.external).map((item) => pathOf(item.to)),
  ), [extraNav]);

  const visibleNav = useMemo(() => NAV_ITEMS.filter((item) => {
    if (item.sports) return settings.settingsLoaded && settings.sports_enabled !== false;
    if (item.optional && shortcutPaths.has(item.to)) return false;
    // Bayraklar tek tek okunur: ``settings`` nesnesinin tamamını bağımlılığa
    // koymak listeyi her renderda yeniden üretir ve ölçüm efektlerini
    // (bar taşması, aktif madde) boşuna tetiklerdi — context değeri her
    // yoklamada yeni bir nesnedir.
    if (item.flag === 'wheel_enabled') return settings.wheel_enabled === true;
    if (item.flag === 'anchor_enabled') return settings.anchor_enabled === true;
    if (item.flag === 'raffle_active') return settings.raffle_active === true;
    if (item.flag === 'prediction_active') return settings.prediction_active === true;
    return !item.category || !hiddenCats.includes(item.category);
  }), [
    hiddenCats, shortcutPaths, settings.settingsLoaded, settings.sports_enabled,
    settings.wheel_enabled, settings.anchor_enabled, settings.raffle_active, settings.prediction_active,
  ]);

  // Oklar yalnızca bar taşarken anlamlı; genişlik hem pencere boyutuyla hem de
  // görünür kategori sayısıyla değiştiği için ikisi de izlenir.
  const measureNav = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setNavOverflow(max > 1);
    setNavEdges({ left: el.scrollLeft > 4, right: max > 4 && el.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    measureNav();
    const el = navRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureNav);
      return () => window.removeEventListener('resize', measureNav);
    }
    const observer = new ResizeObserver(measureNav);
    observer.observe(el);
    // İçerik genişliği yazı tipi yüklenmesiyle de değişebilir.
    Array.from(el.children).forEach((child) => observer.observe(child));
    el.addEventListener('scroll', measureNav, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', measureNav);
    };
  }, [measureNav, visibleNav]);

  // Aktif kategori kaydırma alanının dışında kalabiliyordu; sayfa açıldığında
  // görünür alanın ortasına getirilir. `scrollIntoView` sayfayı da kaydırdığı
  // için yalnızca yatay konum elle ayarlanır.
  useEffect(() => {
    const el = navRef.current;
    const active = el?.querySelector('.vega-nav-item.active');
    if (!el || !active) return;
    const target = active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [location.pathname, visibleNav]);

  // Sol menünün başlıklı grupları panelden gelir. Bağlantısı olmayan grup
  // başlıksız bir boşluk bırakırdı, bu yüzden burada elenir.
  const sideGroups = useMemo(() => (settings.footer_menu_groups || [])
    .map((group) => ({ ...group, links: (group.links || []).filter((item) => item.label) }))
    .filter((group) => group.links.length > 0), [settings.footer_menu_groups]);

  // Alt bardaki gezinme yuvaları panelden gelir; ortadaki giriş/para yatır
  // düğmesi listeye girmez, oturuma göre kendiliğinden değişir.
  const bottomNav = useBottomNavItems();
  const { left: bottomLeft, right: bottomRight } = splitBottomNav(bottomNav);

  const openAuth = (tab) => { setAuthTab(tab); setAuthOpen(true); };

  // Tercih yalnızca oyuncu anahtara bastığında yazılır; kabuk söküldüğünde
  // görünüm koyuya döner ama kayıt korunur (bkz. vegaMode.js).
  const toggleMode = () => setMode((current) => storeVegaMode(current === 'light' ? 'dark' : 'light'));

  // Panelden gelen yuva. "menu" tipi bir adrese gitmez, offcanvas'ı açar —
  // mobilde menüye başka giriş olmadığı için panel bu yuvayı silmemeyi
  // ayrıca hatırlatır.
  const renderBottomItem = (item, index) => {
    const Icon = bottomNavIcon(item.icon);
    const key = `${item.type}-${item.url}-${index}`;
    if (item.type === 'menu') {
      return (
        <button
          key={key}
          type="button"
          className={`vega-bottom-item ${drawerOpen ? 'active' : ''}`}
          onClick={() => setDrawerOpen((open) => !open)}
          aria-expanded={drawerOpen}
        >
          <Icon size={18} />
          <span>{item.label}</span>
        </button>
      );
    }
    return (
      <NavLink
        key={key}
        to={item.url}
        end={item.url === '/'}
        className={({ isActive }) => `vega-bottom-item ${isActive ? 'active' : ''}`}
      >
        <Icon size={18} />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  // Duyuru bandı panelde tanımlı üst bilgi metninden beslenir.
  const announcement = renderSettingText(settings.top_bar_message || '', settings).trim();

  const scrollNav = (direction) => {
    navRef.current?.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  // Kategori maddesi: ikon üstte, etiket altta. Panelden gelen kısayollar dış
  // adres de olabilir; o durumda yönlendirici değil düz bağlantı kullanılır.
  const renderNavItem = (item) => (item.external ? (
    <a
      key={item.to}
      href={item.to}
      target={item.newTab ? '_blank' : undefined}
      rel={item.newTab ? 'noreferrer' : undefined}
      className="vega-nav-item"
    >
      <item.icon size={22} />
      <span>{item.label}</span>
    </a>
  ) : (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      target={item.newTab ? '_blank' : undefined}
      className={({ isActive }) => `vega-nav-item ${isActive ? 'active' : ''}`}
    >
      <item.icon size={22} />
      <span>{item.label}</span>
    </NavLink>
  ));

  // Kısayol şeridi (ikinci kat): daha ince, ikon yanda.
  const renderShortcut = (item) => (item.external ? (
    <a
      key={item.to}
      href={item.to}
      target={item.newTab ? '_blank' : undefined}
      rel={item.newTab ? 'noreferrer' : undefined}
      className="vega-subnav-item"
    >
      <item.icon size={13} />
      {item.label}
    </a>
  ) : (
    <NavLink
      key={item.to}
      to={item.to}
      target={item.newTab ? '_blank' : undefined}
      className={({ isActive }) => `vega-subnav-item ${isActive ? 'active' : ''}`}
    >
      <item.icon size={13} />
      {item.label}
    </NavLink>
  ));

  // Offcanvas maddesi. Sağdaki ok referansın imzasıdır ve her satırda vardır.
  const renderSideLink = (item, key) => (
    <NavLink
      key={key}
      to={item.to}
      end={item.end}
      className={({ isActive }) => `vega-side-link ${isActive ? 'active' : ''}`}
    >
      <item.icon size={17} />
      <span>{item.label}</span>
      <ChevronRight size={15} className="vega-side-arrow" />
    </NavLink>
  );

  return (
    <div className={`vega-shell ${mode === 'light' ? 'is-light' : ''}`}>
      <div className="app-top-bar vega-header-holder">
        <header id={SHELL_IDS.header} className="vega-header">
          <div className="vega-header-lead">
            <button
              type="button"
              className="vega-iconbtn"
              onClick={() => setDrawerOpen(true)}
              aria-label="Menüyü aç"
            >
              <Menu size={18} />
            </button>

            <Logo />

            {/* Referansta logonun sağında geri ve arama düğmeleri durur;
                geri düğmesi dar ekranda gizlenir (tarayıcının kendi geri
                hareketi orada zaten var). */}
            <button
              type="button"
              className="vega-iconbtn vega-back"
              onClick={() => nav(-1)}
              aria-label="Geri"
              title="Geri"
            >
              <ArrowLeft size={17} />
            </button>
            <button
              type="button"
              className="vega-iconbtn"
              onClick={() => setSearchOpen(true)}
              aria-label="Oyun ara"
              title="Oyun ara"
            >
              <Search size={17} />
            </button>
          </div>

          <div className="vega-header-links">
            <button
              type="button"
              className="vega-btn vega-btn-ghost"
              onClick={() => nav('/promosyon-kodu')}
            >
              <Ticket size={15} /> Promo Kod
            </button>
          </div>

          <div className="vega-header-actions">
            {user ? (
              <>
                <button
                  type="button"
                  className="vega-btn vega-btn-deposit"
                  onClick={() => nav('/hesap/para-yatir')}
                  aria-label="Para Yatır"
                >
                  <Plus size={16} />
                  <span className="vega-btn-text">Para Yatır</span>
                </button>
                {/* Bakiye kutusu: üstte etiket, altta tutar. Referansta
                    kullanıcı adı üstte durur; dar ekranda etiketi CSS gizler. */}
                <div className="vega-balance">
                  <Wallet size={15} style={{ color: 'var(--pusu-gold)' }} />
                  <span className="vega-balance-body">
                    <span className="vega-balance-label">Bakiye</span>
                    <span className="vega-balance-value">{Number(balance).toFixed(2)} ₺</span>
                  </span>
                </div>
                {/* Bonus kutusu ancak geniş ekranda sığar; görünürlüğü CSS yönetir. */}
                <div className="vega-balance vega-balance-bonus">
                  <Gift size={15} style={{ color: 'var(--pusu-gold)' }} />
                  <span className="vega-balance-body">
                    <span className="vega-balance-label">Bonus</span>
                    <span className="vega-balance-value">{Number(bonusBalance).toFixed(2)} ₺</span>
                  </span>
                </div>
                <UserDropdown />
                <button
                  type="button"
                  className="vega-iconbtn"
                  onClick={logout}
                  aria-label="Çıkış yap"
                  title="Çıkış yap"
                >
                  <LogOut size={16} />
                </button>
              </>
            ) : (
              <>
                {settings.registration_open !== false && (
                  <button
                    type="button"
                    className="vega-btn vega-btn-ghost vega-btn-register"
                    onClick={() => openAuth('register')}
                  >
                    Üye Ol
                  </button>
                )}
                <button
                  type="button"
                  className="vega-btn vega-btn-accent"
                  onClick={() => openAuth('login')}
                >
                  <LogIn size={15} /> Giriş Yap
                </button>
              </>
            )}
          </div>
        </header>

        {/* Birinci kat: ikonlu ana kategoriler. */}
        <nav
          id={SHELL_IDS.nav}
          className={`vega-nav ${navEdges.left ? 'has-left' : ''} ${navEdges.right ? 'has-right' : ''}`}
          aria-label="Kategoriler"
        >
          {navOverflow && (
            <button
              type="button"
              className="vega-nav-arrow"
              onClick={() => scrollNav(-1)}
              aria-label="Sola kaydır"
            >
              <ChevronLeft size={15} />
            </button>
          )}
          <div className="vega-nav-scroll" ref={navRef}>
            {visibleNav.map(renderNavItem)}
            {user?.is_admin && showAdminLink && (
              <NavLink to="/admin" className="vega-nav-item">
                <ShieldCheck size={22} />
                <span>Yönetim</span>
              </NavLink>
            )}
          </div>
          {navOverflow && (
            <button
              type="button"
              className="vega-nav-arrow"
              onClick={() => scrollNav(1)}
              aria-label="Sağa kaydır"
            >
              <ChevronRight size={15} />
            </button>
          )}
        </nav>

        {/* İkinci kat: panelden gelen kısayollar. Hiç kısayol yoksa şerit
            çizilmez, yoksa başlığın altında boş bir bant kalırdı. */}
        {extraNav.length > 0 && (
          <nav className="vega-subnav" aria-label="Kısayollar">
            <div className="vega-subnav-scroll">
              {extraNav.map(renderShortcut)}
            </div>
          </nav>
        )}
      </div>

      {announcement && !noticeClosed && (
        <div className="vega-notice">
          <span className="vega-notice-icon"><Megaphone size={15} /></span>
          <p className="vega-notice-text">{announcement}</p>
          <button
            type="button"
            className="vega-notice-close"
            onClick={() => setNoticeClosed(true)}
            aria-label="Duyuruyu kapat"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div id={SHELL_IDS.main} className="vega-content">{children}</div>

      <VegaFooter />

      {/* Sol offcanvas: referansın ikinci gezinme yolu. Üst bardaki
          kategorileri başlıklı gruplar hâlinde tekrarlar, ardından bilgi
          sayfaları, sosyal bağlantılar ve görünüm anahtarı gelir. */}
      <aside
        className={`vega-drawer ${drawerOpen ? 'is-open' : ''}`}
        aria-hidden={!drawerOpen}
      >
        <div className="vega-drawer-head">
          <Logo />
          <button
            type="button"
            className="vega-drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Menüyü kapat"
          >
            <X size={18} />
          </button>
        </div>

        <div className="vega-drawer-scroll">
          {user && (
            <>
              <div className="vega-side-heading">Hesabım</div>
              {renderSideLink({ to: '/hesap', label: 'Hesap Özeti', icon: User }, 'account')}
              {renderSideLink(
                { to: '/hesap/para-yatir', label: 'Para Yatır', icon: Plus },
                'deposit',
              )}
              {renderSideLink(
                { to: '/hesap/para-cek', label: 'Para Çek', icon: Wallet },
                'withdraw',
              )}
            </>
          )}

          <div className="vega-side-heading">Oyunlar</div>
          {visibleNav.map((item) => renderSideLink(item, item.to))}

          {/* Panelden gelen menü grupları: başlık + bağlantılar. Bağlantısı
              olmayan grup başlıksız bir boşluk bırakırdı, bu yüzden elenir. */}
          {sideGroups.map((group, groupIndex) => (
            <React.Fragment key={`${group.title}-${groupIndex}`}>
              <div className="vega-side-heading">
                {renderSettingText(group.title, settings) || 'Diğer'}
              </div>
              {group.links.map((item, itemIndex) => {
                const Icon = topNavIcon(item.icon);
                const label = renderSettingText(item.label, settings);
                const key = `${group.title}-${item.url}-${itemIndex}`;
                // Site içi adres yönlendiriciyle, dış adres düz bağlantıyla
                // açılır; yeni sekme işareti panelden gelir.
                return (item.url || '').startsWith('/') && !item.new_tab ? (
                  renderSideLink({ to: item.url, label, icon: Icon }, key)
                ) : (
                  <a
                    key={key}
                    href={item.url || '#'}
                    target={item.new_tab ? '_blank' : undefined}
                    rel={item.new_tab ? 'noreferrer' : undefined}
                    className="vega-side-link"
                  >
                    <Icon size={17} />
                    <span>{label}</span>
                    <ChevronRight size={15} className="vega-side-arrow" />
                  </a>
                );
              })}
            </React.Fragment>
          ))}

          {user?.is_admin && showAdminLink && (
            <>
              <div className="vega-side-heading">Yönetim</div>
              {renderSideLink(
                { to: '/admin', label: 'Yönetim Paneli', icon: ShieldCheck },
                'admin',
              )}
            </>
          )}

          {/* Görünüm anahtarı referansta menünün dibindedir: iki etiket
              arasında kaydırmalı bir düğme. */}
          <div className="vega-mode-switch">
            <span>Koyu</span>
            <button
              type="button"
              className={`vega-mode-track ${mode === 'light' ? 'is-light' : ''}`}
              onClick={toggleMode}
              aria-label={mode === 'light' ? 'Koyu görünüme geç' : 'Açık görünüme geç'}
              aria-pressed={mode === 'light'}
            >
              <span className="vega-mode-knob">
                {mode === 'light' ? <Sun size={12} /> : <Moon size={12} />}
              </span>
            </button>
            <span>Açık</span>
          </div>
        </div>

        {/* İkisi de kapalıysa menü dibinde boş bir şerit kalmasın. */}
        {(callbackEnabled || liveChatEnabled) && (
          <div className="vega-drawer-foot">
            {callbackEnabled && (
              <button
                type="button"
                className="vega-drawer-chip"
                onClick={() => { setDrawerOpen(false); setCallbackOpen(true); }}
              >
                <Phone size={16} />
                <span>Beni Ara</span>
              </button>
            )}
            {liveChatEnabled && (
              <button
                type="button"
                className="vega-drawer-chip"
                onClick={() => { setDrawerOpen(false); openLiveChat(); }}
              >
                <Headphones size={16} />
                <span>Canlı Destek</span>
              </button>
            )}
          </div>
        )}
      </aside>

      {drawerOpen && (
        <div
          className="vega-drawer-backdrop"
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Alt bar beş yuvadır: panelden gelen dört yuva ile ortadaki sabit
          giriş/para yatır düğmesi. */}
      <nav id={SHELL_IDS.bottomBar} className="app-bottom-bar vega-bottom-bar" aria-label="Alt menü">
        {bottomLeft.map(renderBottomItem)}
        {user ? (
          <NavLink to="/hesap/para-yatir" className="vega-bottom-item is-wallet">
            <Wallet size={19} />
            <span>Para Yatır</span>
          </NavLink>
        ) : (
          <button
            type="button"
            className="vega-bottom-item is-wallet"
            onClick={() => openAuth('login')}
          >
            <Wallet size={19} />
            <span>Giriş Yap</span>
          </button>
        )}
        {bottomRight.map(renderBottomItem)}
      </nav>

      {searchOpen && (
        <div
          className="fixed inset-0 z-[70] flex justify-center px-4 pt-24"
          style={{ background: 'rgba(0, 0, 0, 0.72)' }}
          onClick={() => setSearchOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-2xl"
            onClick={(event) => event.stopPropagation()}
            role="presentation"
          >
            <GameSearch onPlay={(game) => { setSearchOpen(false); launcher.play(game); }} />
          </div>
        </div>
      )}

      <GameChoiceModal
        game={launcher.choice}
        user={launcher.user}
        balance={launcher.balance}
        bonusBalance={launcher.bonusBalance}
        demoAvailable={launcher.demoAvailable}
        onClose={launcher.closeChoice}
        onReal={launcher.startReal}
        onDemo={launcher.startDemo}
        onLogin={() => launcher.openAuth('login')}
        onRegister={() => launcher.openAuth('register')}
      />
      {launcher.activeGame && (
        <GameModal
          key={`${launcher.activeGame.id}-${launcher.mode}`}
          game={launcher.activeGame}
          mode={launcher.mode}
          onClose={launcher.closeGame}
        />
      )}

      <AuthModal
        open={authOpen || !!launcher.authTab}
        onClose={() => { setAuthOpen(false); launcher.setAuthTab(null); }}
        defaultTab={launcher.authTab || authTab}
        registrationOpen={settings.registration_open !== false}
      />
      <CallbackModal open={callbackEnabled && callbackOpen} onClose={() => setCallbackOpen(false)} />
    </div>
  );
};

export default VegaLayout;
